import * as React from 'react'
import { useParams } from 'react-router-dom'

import { F0ActionBar } from '@factorialco/f0-react/dist/experimental'
import { Delete, Replace } from '@factorialco/f0-react/icons/app'
import {
  Background,
  Controls,
  type OnEdgesChange,
  type OnNodesChange,
  ReactFlow,
  type Edge as ReactFlowEdge,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { TreeFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import Edge from 'modules/job_catalog/components/DevJobCatalogTree/components/Edge'
import JobCatalogLevelNode from 'modules/job_catalog/components/DevJobCatalogTree/components/JobCatalogLevelNode'
import Node from 'modules/job_catalog/components/DevJobCatalogTree/components/Node'
import { TreeContext } from 'modules/job_catalog/components/DevJobCatalogTree/context'
import { type CustomNode } from 'modules/job_catalog/components/DevJobCatalogTree/types'
import useExpandCollapse from 'modules/job_catalog/components/DevJobCatalogTree/useExpandCollapse'
import useMultiSelect from 'modules/job_catalog/components/DevJobCatalogTree/useMultiSelect'
import useNodeNavigation from 'modules/job_catalog/components/DevJobCatalogTree/useNodeNavigation'
import useNodeSelectionFromRoute from 'modules/job_catalog/components/DevJobCatalogTree/useNodeSelectionFromRoute'
import useToggleExpand from 'modules/job_catalog/components/DevJobCatalogTree/useToggleExpand'

import useTreeDataToNodes from './useTreeDataToNodes'

interface Props {
  width: number
  height: number
  treeData: TreeFragment
}

const JobCatalogTree = ({ width, height, treeData }: Props) => {
  const params = useParams<{ id?: string }>()
  const initialData = useTreeDataToNodes(treeData)
  const [nodes, setNodes] = React.useState<CustomNode[]>(initialData.nodes)
  const [edges, setEdges] = React.useState<ReactFlowEdge[]>(initialData.edges)
  const { onNodeClick } = useNodeNavigation()
  const { onToggleExpand } = useToggleExpand({ setNodes })
  const { checkedNodes, onMultiSelectNode, onUnselectAll } = useMultiSelect({
    nodes,
    edges,
    setNodes,
  })
  const { nodes: visibleNodes, edges: visibleEdges } = useExpandCollapse(nodes, edges)

  useNodeSelectionFromRoute({
    nodeId: params.id,
    setNodes,
    edges,
  })

  const onNodesChange: OnNodesChange<CustomNode> = React.useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  )
  const onEdgesChange: OnEdgesChange = React.useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  )

  const edgeTypes = {
    'custom-edge': Edge,
  }

  const nodeTypes = {
    'custom-node': Node,
    'custom-level-node': JobCatalogLevelNode,
  }

  return (
    <TreeContext.Provider value={{ onToggleExpand, onMultiSelectNode }}>
      <ReactFlow
        fitView
        nodes={visibleNodes}
        edges={visibleEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        width={width}
        height={height}
        minZoom={0.2}
        maxZoom={2}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onlyRenderVisibleElements={false}
        onNodeClick={onNodeClick}
        proOptions={{
          hideAttribution: true,
        }}
      >
        <F0ActionBar
          isOpen={checkedNodes.length > 0}
          label={i18n.tp('job_catalog.job_catalog_tree.action_bar.label', {
            count: checkedNodes.length,
          })}
          primaryActions={[
            {
              label: i18n.t('job_catalog.job_catalog_tree.action_bar.primary_actions.move_to'),
              onClick: () => {},
              icon: Replace,
            },
          ]}
          secondaryActions={[
            { label: i18n.t('common.delete'), onClick: () => {}, icon: Delete, critical: true },
            { label: i18n.t('common.unselect'), onClick: onUnselectAll },
          ]}
        />
        <Controls showInteractive={false} position="top-left" />
        <Background bgColor="#F9F9FB" color="#BFBFBF" />
      </ReactFlow>
    </TreeContext.Provider>
  )
}

const DevJobCatalogTree = ({ width, height, treeData }: Props) => {
  return (
    <ReactFlowProvider>
      <JobCatalogTree width={width} height={height} treeData={treeData} />
    </ReactFlowProvider>
  )
}

export default DevJobCatalogTree
